export type LayoutViewMode = "desktop" | "tablet" | "mobile";

export interface FrameViewportModel {
  visible?: boolean;
  colSpan?: number;
  minHeight?: "auto" | "sm" | "md" | "lg" | "xl";
  cssClass?: string;
}

export interface LayoutFrameModel {
  id: string;
  name?: string;
  row?: number;
  desktop?: FrameViewportModel;
  tablet?: FrameViewportModel;
  mobile?: FrameViewportModel;
}

const GRID_COLUMNS = 12;

const COL_SPAN_CLASS: Record<number, string> = {
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
  5: "col-span-5",
  6: "col-span-6",
  7: "col-span-7",
  8: "col-span-8",
  9: "col-span-9",
  10: "col-span-10",
  11: "col-span-11",
  12: "col-span-12",
};

const MIN_HEIGHT_CLASS: Record<string, string> = {
  auto: "",
  sm: "min-h-16",
  md: "min-h-32",
  lg: "min-h-48",
  xl: "min-h-64",
};

/** Layout shell frames are sized by their components, not grid min-height. */
const FRAME_MIN_HEIGHT_SKIP_IDS = new Set(["topbar", "header", "footer"]);

export const clampColSpan = (value?: number) => {
  const span = Number(value) || 12;
  return Math.min(GRID_COLUMNS, Math.max(1, span));
};

export const getFrameViewport = (frame: LayoutFrameModel, viewMode: LayoutViewMode) => {
  const viewport = frame[viewMode] || frame.desktop || {};
  return {
    visible: viewport.visible !== false,
    colSpan: clampColSpan(viewport.colSpan ?? 12),
    minHeight: viewport.minHeight ?? "auto",
    cssClass: viewport.cssClass?.trim() || "",
  };
};

export const isFrameVisible = (frame: LayoutFrameModel, viewMode: LayoutViewMode) =>
  getFrameViewport(frame, viewMode).visible !== false;

export const groupFramesByRow = (frames: LayoutFrameModel[] = []) => {
  const rowMap = new Map<number, LayoutFrameModel[]>();
  frames.forEach((frame) => {
    const row = frame.row || 1;
    if (!rowMap.has(row)) rowMap.set(row, []);
    rowMap.get(row)!.push(frame);
  });
  return Array.from(rowMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([row, rowFrames]) => ({ row, frames: rowFrames }));
};

export const frameToClassName = (
  viewport: ReturnType<typeof getFrameViewport>,
  frame?: Pick<LayoutFrameModel, "id" | "name">,
) => {
  const colSpan = clampColSpan(viewport.colSpan);
  const frameKey = (frame?.id || frame?.name || "").trim().toLowerCase();
  const skipMinHeight = FRAME_MIN_HEIGHT_SKIP_IDS.has(frameKey);
  const minHeight = skipMinHeight
    ? ""
    : MIN_HEIGHT_CLASS[viewport.minHeight || "auto"] || MIN_HEIGHT_CLASS.auto;
  const cssClass = viewport.cssClass?.trim() || "";
  return [COL_SPAN_CLASS[colSpan], minHeight, cssClass].filter(Boolean).join(" ");
};

export const isFramedSiteDocument = (document: {
  layoutVersion?: number;
  layoutFrames?: LayoutFrameModel[];
}) => Boolean(document.layoutVersion === 2 && document.layoutFrames?.length);

export const groupComponentsByFrameId = (components: any[] = []) => {
  const map = new Map<string, any[]>();
  components.forEach((component) => {
    const frameId = component?.__frame?.id;
    if (!frameId) return;
    if (!map.has(frameId)) map.set(frameId, []);
    map.get(frameId)!.push(component);
  });
  return map;
};

/** V2 merge for mock JSON — mirrors be-dash inquiry framed merge. */
export const mergeFramedLayoutDocumentComponents = (
  layoutComponents: any[] = [],
  docComponents: any[] = [],
  frames: LayoutFrameModel[] = [],
) => {
  const sorted = [...frames].sort((a, b) => {
    const rowDiff = (a.row || 0) - (b.row || 0);
    if (rowDiff !== 0) return rowDiff;
    return a.id.localeCompare(b.id);
  });

  const withFrames = layoutComponents.map((component, index) => ({
    ...component,
    __frame: component.__frame ?? sorted[index],
  }));

  return withFrames.flatMap((component) => {
    if (component.$type === "layout-children") {
      if (!docComponents.length) {
        return [
          {
            $type: "layout-children",
            __frame: component.__frame,
            __sbEmpty: true,
          },
        ];
      }
      return docComponents.map((docComponent: any) => ({
        ...docComponent,
        __frame: component.__frame,
      }));
    }

    return [component];
  });
};
